/**
 * Individual PO Line row component for the PO Lines table.
 * Renders dynamic column cells based on visibility configuration.
 */

import { AlertCircle } from 'lucide-react';
import { ConcessionStatusBadge } from '../../ConcessionStatusBadge';
import { PurchaseOrderLine, ColumnConfig, NextActionResult } from './types';
import { getLineStatusBadge, isFullyInSupplierWarehouse } from './utils';
import { POLineRowActions } from './POLineRowActions';
import { renderQuantityCell } from './POLineRowQtyCells';
import CallOffSupplyCell from './CallOffSupplyCell';
import { formatAmount } from '../../../../lib/amountDisplay';
import { formatUnitPrice } from '../../../../lib/unitPrice';
import { formatExchangeRateValue } from '../../../../lib/exchangeRateDisplay';

import type { User } from '../../../../lib/api';
import { useFormatters } from '../../../../contexts/PreferencesContext';
import { useMenuSettings } from '../../../../contexts/MenuSettingsContext';
import { getOtifDateVariance } from '../../../../utils/otifDateVariance';
import { OtifDecisionBadge } from '../../otifDecision';
/**
 * Contract prices carry three decimals, and did before this item touched them.
 * Aaron's plain-number ruling (walkthrough 26/27) named the unit price, the
 * exchange rate, the total and the O/S value — the four cells he was looking at
 * — and the sweep that applied it took a decimal place off these three as well
 * (F12, gate round 1). A contract rate of 0.125 is not 0.13.
 */
const CONTRACT_PRICE_DECIMALS = 3;

/** Human-readable hint for raw ERP order-type codes (shown as a tooltip). */
const ERP_ORDER_TYPE_LABELS: Record<string, string> = {
  '1': 'Standard / spot PO',
  '4': 'Scheduling agreement',
};

interface POLineRowProps {
  line: PurchaseOrderLine;
  user: User | null;
  columns: ColumnConfig[];
  selectedLineIds: Set<number>;
  formatDate: (date: string | Date, options?: { format?: string; includeTime?: boolean }) => string;
  shouldShowActions: (line: PurchaseOrderLine) => boolean;
  shouldShowDecline: (line: PurchaseOrderLine) => boolean;
  shouldShowProposeChange: (line: PurchaseOrderLine) => boolean;
  getNextAction: (line: PurchaseOrderLine) => NextActionResult;
  canSelectForBulkAccept: (line: PurchaseOrderLine) => boolean;
  onSelectLine: (lineId: number) => void;
  onReviewChanges: (lineId: number) => void;  // CR-4728
  onSelectLineForWorkflow: (line: PurchaseOrderLine, modal: 'accept' | 'decline') => void;
  onProposeChange: (line: PurchaseOrderLine) => void;
  onToggleCritical: (line: PurchaseOrderLine) => void;
  onPutOnHold: (line: PurchaseOrderLine) => void;
  onReleaseHold: (line: PurchaseOrderLine) => void;
  onToggleSelect: (lineId: number) => void;
  /** CR-7418: the OTIF Review pill is active — see POLineRowActions. */
  otifReview?: boolean;
  onOtifDone?: () => void;
  /** No selection cell: schedule collaboration, or a supplier on the OTIF Review pill. */
  hideSelect?: boolean;
  // CR-4687 round 3: when ``true``, two or more rows are selected so per-line
  // action controls (Accept / Decline / ReviewChanges / Propose Change /
  // Put on hold / Release hold) are rendered disabled. Bulk actions are the
  // only allowed path in this mode — prevents a tester from acknowledging
  // one line while three are checked, which previously left the selection
  // count out of sync with the underlying state.
  bulkMode?: boolean;
}

export function POLineRow({
  line,
  user,
  columns,
  selectedLineIds,
  formatDate,
  shouldShowActions,
  shouldShowDecline,
  shouldShowProposeChange,
  getNextAction,
  canSelectForBulkAccept,
  onSelectLine,
  onReviewChanges,
  onSelectLineForWorkflow,
  onProposeChange,
  onToggleCritical: _onToggleCritical,
  onPutOnHold,
  onReleaseHold,
  onToggleSelect,
  otifReview = false,
  onOtifDone,
  hideSelect = false,
  bulkMode = false,
}: POLineRowProps) {
  const bulkModeTitle = 'Disabled — multiple lines selected. Use bulk actions or clear selection.';
  const nextAction = getNextAction(line);
  // Conv. columns are held in the customer's reporting currency
  // (line.converted_currency — INR for an INR-based tenant, GBP for most).
  // formatCurrencyFromBase CONVERTS them into the user's preferred currency
  // when a rate exists, else labels them in the reporting currency.
  //
  // Batch 4 swapped this for formatAmount and called it cosmetic. It was not:
  // formatAmount only formats, so the Conv. columns were rendering the RAW
  // stored figure as if it had been converted, and there is no Converted
  // Currency column to disclose which currency the number is even in. The
  // plain-number rule is about a glyph that duplicates an adjacent Currency
  // column; these columns have no such column, and the conversion is the
  // column's entire purpose. Reverted.
  const { formatCurrencyFromBase, formatQuantity } = useFormatters();
  const formatConverted = (value: string) =>
    formatCurrencyFromBase(parseFloat(value), undefined, line.converted_currency);
  const { isFeatureEnabled } = useMenuSettings();

  // Supplier-side line closure: when the supplier has uploaded enough
  // warehouse stock to cover the outstanding qty, the line is greyed out
  // and supplier actions are suppressed. Gated on call-off because
  // suppliers without it have no warehouse-allocation concept. Customer
  // view is unaffected — they still need to GRN to close the order.
  const isSupplier = user?.platform === 'SUPPLIER';
  const inSupplierStock = isSupplier
    && isFeatureEnabled('switch_call_off_tab')
    && isFullyInSupplierWarehouse(line);

  const renderCell = (columnId: string) => {
    switch (columnId) {
      case 'acknowledge_status':
        return (
          <td key={columnId} className="py-3 px-4">
            {/* EXPEDITING-PARITY PR 2 — the line's ONE status, hold included.
                Aaron, 17 Sep: *"the status IS On Hold (Customer/Supplier), it
                cannot be both on hold and in motion... that fixes some of these
                issues we have in the UI where we have multiple badges
                contradicting themselves."*

                Two earlier positions, both superseded. This cell originally
                substituted "Closed" (CR-4696) and "On Hold" into the
                acknowledge status, which lost the acknowledge status entirely
                and made a release look like a revert to New. On 16 Sep it was
                narrowed to the acknowledge status ALONE, with the hold given
                its own column — which is what produced the contradicting
                badges he found the next day. Now: statusOf answers once, the
                hold wins when there is one, and the acknowledge status is the
                resume point in the data rather than a second badge on screen.

                SUPERSEDED 18 Sep 2026 (CR-7412). This comment used to end
                "Receipt state is not lost either — it has its own Open / Closed
                tabs and greys the whole row", and that was the whole mistake: a
                tab is a filter and a grey row is an emphasis, neither is a
                STATUS, and this is the cell a buyer reads. Amber reproduced it on
                Gardner and AC within hours of the merge — 1,978 lines under
                Status = Closed, every one badged blue "New". `statusOf` now
                answers the receipt state FIRST, above the hold, which is legacy's
                own order. Nothing was added to this cell to achieve it, and the
                grey has gone from the row too: see the <tr> className below. */}
            {getLineStatusBadge(line)}
          </td>
        );

      case 'action':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            <POLineRowActions
              line={line}
              user={user}
              bulkMode={bulkMode}
              bulkModeTitle={bulkModeTitle}
              shouldShowActions={shouldShowActions}
              shouldShowDecline={shouldShowDecline}
              shouldShowProposeChange={shouldShowProposeChange}
              onReviewChanges={onReviewChanges}
              onSelectLineForWorkflow={onSelectLineForWorkflow}
              onProposeChange={onProposeChange}
              onPutOnHold={onPutOnHold}
              onReleaseHold={onReleaseHold}
              otifReview={otifReview}
              onOtifDone={onOtifDone}
            />
          </td>
        );

      case 'is_critical':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.is_critical ? (
              <span className="p-1.5 rounded bg-amber-100 inline-flex" title="Critical">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </span>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </td>
        );

      // RETIRED 17 Sep 2026 (Aaron): the 'on_hold' column is gone from
      // DEFAULT_COLUMNS, because a held line's STATUS is the hold and a second
      // column saying the same thing is how two badges come to disagree. Its
      // cell is deleted rather than left unreachable — the hold is rendered by
      // the Status cell above, so nothing here is dormant code with a reader.
      // Hold Reason below is a different fact (the WHY) and stays.

      case 'hold_reason':
        return (
          <td key={columnId} className="py-3 px-4">
            {line.on_hold ? (
              <div className="text-sm">{line.on_hold_reason_text || line.on_hold_reason_details?.description || 'On hold'}</div>
            ) : (
              <span className="text-xs text-muted-foreground">-</span>
            )}
          </td>
        );

      case 'next_action':
        return (
          <td key={columnId} className="py-3 px-4">
            <div className="flex justify-center">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${nextAction.className}`}>{nextAction.text}</span>
            </div>
          </td>
        );

      case 'concession_status':
        return (
          <td key={columnId} className="py-3 px-4">
            <div className="flex justify-center">
              <ConcessionStatusBadge status={line.concession_status} customerId={line.customer_id} compact />
            </div>
          </td>
        );

      case 'po_number':
        return (<td key={columnId} className="py-3 px-4"><span className="font-medium text-sm">{line.po_number || `PO-${line.po}`}</span></td>);

      case 'line_number':
        return (<td key={columnId} className="py-3 px-4 text-center"><span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">{line.line_number}</span></td>);

      case 'erp_order_type':
        return (
          <td key={columnId} className="py-3 px-4 text-sm">
            {line.erp_order_type
              ? <span className="px-2 py-1 bg-muted rounded text-xs font-medium" title={ERP_ORDER_TYPE_LABELS[line.erp_order_type] || `ERP type ${line.erp_order_type}`}>{line.erp_order_type}</span>
              : <span className="text-muted-foreground text-xs">-</span>}
          </td>
        );

      case 'supplier_name':
        return (
          <td key={columnId} className="py-3 px-4 text-sm">
            {/* CR-4717: Plain text, no hyperlink */}
            <span>{line.supplier_name || 'N/A'}</span>
          </td>
        );

      case 'customer_name':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.customer_name || 'N/A'}</td>);
      case 'part_number':
        return (<td key={columnId} className="py-3 px-4"><span className="font-mono text-sm font-medium">{line.part_number}</span></td>);
      case 'part_description':
        return (<td key={columnId} className="py-3 px-4"><div className="text-sm">{line.part_description}</div></td>);
      case 'description':
        return (<td key={columnId} className="py-3 px-4"><div className="text-sm">{line.description}</div></td>);
      case 'quantity':
        return (<td key={columnId} className="py-3 px-4 text-right"><span className="text-sm font-medium">{formatQuantity(line.quantity)}</span></td>);
      case 'received_qty':
      case 'delivered_qty':
      case 'rejected_qty':
      case 'delivered_warehouse_qty':
      case 'warehouse_fulfilment_pct':
      case 'otif_verdict':
      case 'outstanding_qty':
      case 'pct_delivered':
        return renderQuantityCell(columnId, line, formatQuantity);
      // CR-7743: what the supplier still has to make, and the days left to make it.
      case 'remaining_to_produce_qty':
      case 'days_left_to_produce':
        return <CallOffSupplyCell key={columnId} columnId={columnId} line={line} />;
      case 'unit_price':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm">{formatUnitPrice(line.unit_price) || '-'}</td>);
      case 'total_price':
        return (<td key={columnId} className="py-3 px-4 text-right font-medium">{formatAmount(line.total_price) || '-'}</td>);
      case 'delivery_date':
        // The Customer Required Date — the date expediting moves (DATE-FIELD-CANON PR 2).
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.customer_required_date ? formatDate(line.customer_required_date) : '-'}</td>);
      case 'odd_date': {
        // ODD column: the server's odd_date, not the stored Customer Required Date;
        // falls back to the CRD only when the payload carries no odd_date.
        const odd = line.odd_date ?? line.customer_required_date;
        return (<td key={columnId} className="py-3 px-4 text-sm">{odd ? formatDate(odd) : '-'}</td>);
      }
      case 'supplier_promise_date': {
        // CR-5479: amber when supplier-proposed date is earlier than the
        // ODD's OTIF window allows; red when it's later. Shared with the
        // Change Request views via getOtifDateVariance so the table and the
        // drill-down colour/variance stay identical.
        if (!line.supplier_promise_date) {
          return (<td key={columnId} className="py-3 px-4 text-sm"></td>);
        }
        const variance = getOtifDateVariance(
          line.supplier_promise_date, line.odd_date ?? line.customer_required_date, line.supplier_otif_window,
        );
        const cls = variance?.className || '';
        const suffix = variance?.suffix || '';
        return (
          <td key={columnId} className="py-3 px-4 text-sm">
            <span className={cls ? `inline-block px-2 py-0.5 rounded ${cls}` : ''}>
              {formatDate(line.supplier_promise_date)}{suffix}
            </span>
          </td>
        );
      }
      case 'buyer_id':
        // CR-7400: RTV (return to vendor) is a buyer CODE, not a person, and
        // the Supplier Portal shows it in red (OpenOrders.js FormatBuyerID,
        // `val == 'RTV'`). Same exact match here, and the same display for
        // every customer.
        return (
          <td key={columnId} className="py-3 px-4 text-sm">
            {line.buyer_id ? (
              <span className={line.buyer_id === 'RTV' ? 'text-red-600 font-medium' : ''}>
                {line.buyer_id}
              </span>
            ) : '-'}
          </td>
        );
      case 'customer_name':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.customer_name || '-'}</td>);
      case 'currency':
        return (<td key={columnId} className="py-3 px-4 text-sm text-center"><span className="px-2 py-1 bg-muted rounded text-xs font-medium">{line.currency || '-'}</span></td>);
      case 'batch_serial_no':
        return (<td key={columnId} className="py-3 px-4 text-sm font-mono">{line.batch_serial_no || '-'}</td>);
      case 'ppv': {
        // CR-5957 round 5: legacy OpenOrdersSQL.cs:230 distinguishes PPV=NULL
        // (no contract match → blank) from PPV=0 (contract matched, no variance
        // → render the literal value). Previous check `parseFloat(line.ppv) !== 0`
        // collapsed both into a dash, making a contract-matched-zero-variance
        // line look identical to a no-contract line.
        const ppvNum = line.ppv != null && line.ppv !== '' ? parseFloat(line.ppv) : null;
        return (
          <td key={columnId} className="py-3 px-4 text-sm text-right">
            {ppvNum === null || Number.isNaN(ppvNum) ? (
              '-'
            ) : ppvNum === 0 ? (
              // Legacy OpenOrdersSQL.cs:230 returns PPV as a plain numeric
              // string with no currency symbol — PPV is a variance KPI, not
              // a price. Render decimals only.
              <span className="inline-block px-2 py-0.5 rounded bg-muted text-foreground font-medium">
                {new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(0)}
              </span>
            ) : (
              <span className={`inline-block px-2 py-0.5 rounded text-white font-medium ${ppvNum > 0 ? 'bg-red-500' : 'bg-amber-500'}`}>
                {new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(ppvNum)}
              </span>
            )}
          </td>
        );
      }
      // CR-7418: the cell the review pill brings with it. The badge prints the
      // decision, or a dash where the line has no review to report — it does
      // NOT call every undecided line "Not Decided", which would put an amber
      // "waiting on someone" chip on every pending line the moment a user
      // turned this column on from another tab.
      case 'otif_review':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            <OtifDecisionBadge line={line} />
          </td>
        );
      case 'supplier_group_name':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.supplier_group_name || '-'}</td>);
      case 'packing_note_number':
        return (<td key={columnId} className="py-3 px-4 text-sm font-mono">{line.packing_note_number || '-'}</td>);
      case 'order_date':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.order_date ? formatDate(line.order_date) : '-'}</td>);
      case 'last_received_date':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.last_received_date ? formatDate(line.last_received_date) : '-'}</td>);
      case 'updated_at':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.updated_at ? formatDate(line.updated_at, { includeTime: true }) : '-'}</td>);
      case 'required_date_change_count':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.required_date_change_count !== undefined && line.required_date_change_count > 0 ? (
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">{line.required_date_change_count}</span>
            ) : (<span className="text-muted-foreground">-</span>)}
          </td>
        );
      case 'promised_date_change_count':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.promised_date_change_count !== undefined && line.promised_date_change_count > 0 ? (
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-800 text-xs font-medium">{line.promised_date_change_count}</span>
            ) : (<span className="text-muted-foreground">-</span>)}
          </td>
        );
      case 'pdf_attached_count':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.pdf_attached_count !== undefined && line.pdf_attached_count > 0 ? (
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-800 text-xs font-medium">{line.pdf_attached_count}</span>
            ) : (<span className="text-muted-foreground">-</span>)}
          </td>
        );
      case 'material_cert_count':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.material_cert_count !== undefined && line.material_cert_count > 0 ? (
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-teal-100 text-teal-800 text-xs font-medium">{line.material_cert_count}</span>
            ) : (<span className="text-muted-foreground">-</span>)}
          </td>
        );
      case 'contract_unit_price':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm">{formatAmount(line.contract_unit_price, CONTRACT_PRICE_DECIMALS) || '-'}</td>);
      case 'contract_lot_price':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm">{formatAmount(line.contract_lot_price, CONTRACT_PRICE_DECIMALS) || '-'}</td>);
      case 'drawing_number':
        return (<td key={columnId} className="py-3 px-4 text-sm font-mono">{line.drawing_number || '-'}</td>);
      case 'drawing_version':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.drawing_version || '-'}</td>);
      case 'outstanding_value':
        return (
          <td key={columnId} className="py-3 px-4 text-right text-sm">
            {line.outstanding_value ? (<span className={parseFloat(line.outstanding_value) > 0 ? 'text-orange-600 font-medium' : ''}>{formatAmount(line.outstanding_value)}</span>) : '-'}
          </td>
        );
      case 'converted_unit_price':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm">{line.converted_unit_price ? formatConverted(line.converted_unit_price) : '-'}</td>);
      case 'converted_total_value':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm font-medium">{line.converted_total_value ? formatConverted(line.converted_total_value) : '-'}</td>);
      case 'converted_outstanding_value':
        return (
          <td key={columnId} className="py-3 px-4 text-right text-sm">
            {line.converted_outstanding_value ? (<span className={parseFloat(line.converted_outstanding_value) > 0 ? 'text-orange-600 font-medium' : ''}>{formatConverted(line.converted_outstanding_value)}</span>) : '-'}
          </td>
        );
      case 'original_promise_date':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.original_required_date ? formatDate(line.original_required_date) : '-'}</td>);
      case 'mrp_scheduled_date':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.mrp_scheduled_date ? formatDate(line.mrp_scheduled_date) : '-'}</td>);
      case 'priority':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.priority ? (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                line.priority === 'critical' ? 'bg-red-100 text-red-800' :
                line.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                line.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>{line.priority.charAt(0).toUpperCase() + line.priority.slice(1)}</span>
            ) : (<span className="text-muted-foreground">-</span>)}
          </td>
        );
      case 'contract_price':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm">{formatAmount(line.contract_price, CONTRACT_PRICE_DECIMALS) || '-'}</td>);
      case 'exchange_rate':
        // CR-7408 rework: the line window's rule (Aaron, 17 Sep: items 26/27).
        // This printed the stored rate raw at six places, so PRO00189/3 read
        // 0.784600 here and 1.00 in the window.
        return (<td key={columnId} className="py-3 px-4 text-right text-sm font-mono">{formatExchangeRateValue(line.exchange_rate, line.currency, line.converted_currency)}</td>);
      default:
        return null;
    }
  };

  // CR-7418 (Aaron, 19 Sep): the closed/received/cancelled row used to be dimmed
  // to `opacity-50 bg-muted/30`. That is gone — "its trash and adds nothing". It
  // was standing in for a status the row could not state, and CR-7412 gave closed
  // lines a badge that says it properly; dimming every cell to repeat what one
  // badge already says only makes the text harder to read — and on the OTIF
  // Review pill EVERY line is received, so the entire grid came up grey. The
  // in-stock dim stays: it means something different (the goods are in the
  // supplier's warehouse, not yet GRN'd) and carries its own tooltip.
  // CR-7463: the sticky selection cell needs its OWN solid background so the
  // data columns scroll underneath it rather than showing through. A row's
  // tint (in-stock slate, forecast amber) is semi-transparent and bg-inherit
  // does not carry it reliably, so mirror the row's base colour here. The
  // group-hover class keeps it in step with the row's hover highlight.
  const stickyCellBg = inSupplierStock
    ? 'bg-slate-50'
    : line.is_forecast
      ? 'bg-amber-50'
      : 'bg-card';

  return (
    <tr
      className={`group border-b hover:bg-muted/50 cursor-pointer transition-colors ${inSupplierStock ? 'opacity-60 bg-slate-50' : ''} ${line.is_forecast ? 'bg-amber-50/50' : ''}`}
      title={line.is_forecast ? `Forecast — outside firm window (${line.firm_days ?? '?'} days)` : inSupplierStock ? 'In Stock — full ordered qty is in your warehouse, awaiting customer GRN.' : undefined}
      onClick={() => onSelectLine(line.id)}
    >
      {!hideSelect && (
        /* CR-7463: pin the selection cell to the left (mirrors the sticky
           header) so the checkbox stays reachable when the table scrolls
           horizontally at tablet width. The explicit background keeps it
           opaque over the row's hover / forecast / in-stock states so data
           columns do not show through as they scroll underneath. */
        <td className={`py-3 px-2 text-center sticky left-0 z-10 ${stickyCellBg} group-hover:bg-muted/50`} onClick={(e) => e.stopPropagation()}>
          {canSelectForBulkAccept(line) ? (
            <input type="checkbox" checked={selectedLineIds.has(line.id)} onChange={() => onToggleSelect(line.id)} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </td>
      )}
      {columns.filter(col => col.visible).map(col => renderCell(col.id))}
      <td className="py-3 px-4 text-center"><span className="text-xs text-primary hover:underline">View →</span></td>
    </tr>
  );
}
